import { useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle2, Sparkles, Package, MapPin, User, ShieldCheck } from "lucide-react";
import { QUOTE } from "@/constants/testIds";
import { SERVICES, ALL_COUNTRIES } from "@/data/turboData";

const STEP_TITLES = [
  { n: 1, label: "Package", icon: Package },
  { n: 2, label: "Route", icon: MapPin },
  { n: 3, label: "Your price", icon: Sparkles },
  { n: 4, label: "Your details", icon: User },
];

const Quote = () => {
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    service: params.get("service") || SERVICES[0].slug,
    weight: params.get("weight") || "",
    length: "",
    width: "",
    height: "",
    units: "in/lb",
    fromCountry: params.get("from") || "United States",
    fromZip: "",
    toCountry: params.get("to") || "Nigeria",
    toZip: "",
    name: "",
    email: "",
    phone: "",
    terms: false,
  });

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const estimate = useMemo(() => {
    const w = parseFloat(form.weight) || 0;
    const svc = SERVICES.find((s) => s.slug === form.service);
    const base = svc?.startsAt?.replace(/[^0-9]/g, "") || "39";
    const price = Math.max(parseInt(base, 10), Math.round(parseInt(base, 10) + w * 4.25));
    const eta = svc?.eta || "—";
    return { price, eta, service: svc };
  }, [form.weight, form.service]);

  const next = () => setStep((s) => Math.min(4, s + 1));
  const back = () => setStep((s) => Math.max(1, s - 1));

  return (
    <div className="pt-12 md:pt-16 pb-10">
      <div className="mx-auto max-w-3xl px-4 sm:px-6">
        <div className="text-xs uppercase tracking-[0.2em] text-[var(--tt-accent)] font-semibold">
          Get a quote
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold text-[var(--tt-navy)] mt-3 leading-[0.98]">
          Tell us about your shipment.
        </h1>

        {/* Stepper */}
        <div className="mt-10">
          <div className="flex items-center justify-between gap-2">
            {STEP_TITLES.map((s, i) => {
              const Active = step >= s.n;
              const Icon = s.icon;
              return (
                <div key={s.n} className="flex-1 flex items-center gap-2">
                  <div className={`flex items-center gap-2 ${Active ? "text-[var(--tt-navy)]" : "text-[var(--tt-muted)]"}`}>
                    <div className={`h-8 w-8 rounded-full grid place-items-center text-xs font-bold transition ${
                      Active ? "bg-[var(--tt-accent)] text-white" : "bg-white border border-[var(--tt-line)]"
                    }`}>
                      {step > s.n ? <CheckCircle2 className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                    </div>
                    <span className="hidden sm:inline text-sm font-semibold">{s.label}</span>
                  </div>
                  {i < STEP_TITLES.length - 1 && (
                    <div className={`flex-1 h-px ${step > s.n ? "bg-[var(--tt-accent)]" : "bg-[var(--tt-line)]"}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Card */}
        <div className="mt-8 card p-6 md:p-8" data-testid={QUOTE.step(step)}>
          {step === 1 && (
            <>
              <h2 className="font-display text-2xl font-bold text-[var(--tt-navy)]">What are you shipping?</h2>
              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                {SERVICES.map((s) => (
                  <button
                    key={s.slug}
                    onClick={() => setF("service", s.slug)}
                    type="button"
                    className={`text-left rounded-xl border p-4 transition ${
                      form.service === s.slug
                        ? "border-[var(--tt-navy)] bg-[var(--tt-bg-soft)]"
                        : "border-[var(--tt-line)] hover:border-[var(--tt-navy)]/40"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-semibold text-[var(--tt-navy)]">{s.name}</div>
                      {form.service === s.slug && <CheckCircle2 className="h-4 w-4 text-[var(--tt-accent)]" />}
                    </div>
                    <div className="text-xs text-[var(--tt-muted)] mt-1">{s.eta}</div>
                    <div className="text-xs font-semibold text-[var(--tt-accent)] mt-1.5">{s.startsAt}</div>
                  </button>
                ))}
              </div>

              <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Field label="Length" value={form.length} onChange={(v) => setF("length", v)} testid={QUOTE.field("length")} placeholder="—" type="number" />
                <Field label="Width" value={form.width} onChange={(v) => setF("width", v)} testid={QUOTE.field("width")} placeholder="—" type="number" />
                <Field label="Height" value={form.height} onChange={(v) => setF("height", v)} testid={QUOTE.field("height")} placeholder="—" type="number" />
                <Field label="Weight (kg)" value={form.weight} onChange={(v) => setF("weight", v)} testid={QUOTE.field("weight")} placeholder="e.g. 12" type="number" />
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="font-display text-2xl font-bold text-[var(--tt-navy)]">Where is it going?</h2>
              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                <Select label="From country" value={form.fromCountry} onChange={(v) => setF("fromCountry", v)} options={ALL_COUNTRIES} testid={QUOTE.field("fromCountry")} />
                <Field label="From postal code" value={form.fromZip} onChange={(v) => setF("fromZip", v)} testid={QUOTE.field("fromZip")} placeholder="48083" />
                <Select label="To country" value={form.toCountry} onChange={(v) => setF("toCountry", v)} options={ALL_COUNTRIES} testid={QUOTE.field("toCountry")} />
                <Field label="To postal code" value={form.toZip} onChange={(v) => setF("toZip", v)} testid={QUOTE.field("toZip")} placeholder="—" />
              </div>
              <div className="mt-5 flex items-start gap-2 text-xs text-[var(--tt-muted)] bg-[var(--tt-bg-soft)] rounded-xl p-3">
                <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-[var(--tt-accent)]" />
                We ship to 180+ countries. Customs &amp; duties are calculated separately
                at delivery based on the destination.
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h2 className="font-display text-2xl font-bold text-[var(--tt-navy)]">Your estimated price</h2>
              <div className="mt-5 rounded-2xl bg-gradient-to-br from-[var(--tt-navy)] to-[var(--tt-navy-2)] text-white p-6 md:p-8">
                <div className="flex items-baseline gap-2">
                  <span className="text-5xl md:text-6xl font-display font-bold">${estimate.price}</span>
                  <span className="text-white/60 text-sm">USD est.</span>
                </div>
                <div className="mt-3 text-white/80 text-sm">
                  {estimate.service?.name} · arrives in {estimate.eta}
                </div>
                <div className="mt-5 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-white/50 text-xs uppercase tracking-wider">Route</div>
                    <div className="mt-1">{form.fromCountry} → {form.toCountry}</div>
                  </div>
                  <div>
                    <div className="text-white/50 text-xs uppercase tracking-wider">Weight</div>
                    <div className="mt-1">{form.weight || "—"} kg</div>
                  </div>
                </div>
                <div className="mt-6 text-xs text-white/60 leading-relaxed">
                  Final price confirmed after our team reviews your declaration.
                  Insurance up to $2,500 available at checkout.
                </div>
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <h2 className="font-display text-2xl font-bold text-[var(--tt-navy)]">Almost there.</h2>
              <p className="text-sm text-[var(--tt-muted)] mt-1">
                Tell us where to send your confirmed quote.
              </p>
              <div className="mt-5 space-y-3">
                <Field label="Full name" value={form.name} onChange={(v) => setF("name", v)} testid={QUOTE.field("name")} placeholder="Jane Doe" />
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Email" value={form.email} onChange={(v) => setF("email", v)} testid={QUOTE.field("email")} placeholder="you@email.com" type="email" />
                  <Field label="Phone" value={form.phone} onChange={(v) => setF("phone", v)} testid={QUOTE.field("phone")} placeholder="+1 248 …" />
                </div>
                <label className="mt-3 flex items-start gap-2 text-sm text-[var(--tt-ink-soft)] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.terms}
                    onChange={(e) => setF("terms", e.target.checked)}
                    data-testid={QUOTE.field("terms")}
                    className="mt-1 h-4 w-4 accent-[var(--tt-accent)]"
                  />
                  I agree to Turbo Transit&apos;s Terms &amp; shipping policies, including
                  prohibited items and the $2,500 maximum declared value.
                </label>
              </div>
              <div className="mt-5 rounded-xl border border-[var(--tt-line)] bg-[var(--tt-bg-soft)] p-3 text-xs text-[var(--tt-muted)] flex items-start gap-2">
                <ShieldCheck className="h-4 w-4 text-[var(--tt-accent)] mt-0.5 shrink-0" />
                <span>
                  <strong>In Wix:</strong> this form will be replaced with a native
                  Wix Form connected to your Wix CRM. Each submission auto-creates
                  a tagged contact (source: website · service:{" "}
                  {estimate.service?.name}).
                </span>
              </div>
            </>
          )}

          {/* Footer nav */}
          <div className="mt-8 flex items-center justify-between gap-3">
            <button
              onClick={back}
              disabled={step === 1}
              data-testid={QUOTE.back}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--tt-muted)] hover:text-[var(--tt-navy)] disabled:opacity-30 disabled:cursor-not-allowed transition"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            {step < 4 ? (
              <button onClick={next} data-testid={QUOTE.next} className="btn-primary">
                Continue <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                onClick={() => alert("Demo only. In Wix this submits to Wix CRM.")}
                disabled={!form.terms}
                data-testid={QUOTE.submit}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Submit quote <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-[var(--tt-muted)]">
          Need to ship freight or bulk pallets?{" "}
          <Link to="/contact" className="text-[var(--tt-navy)] font-semibold underline-offset-2 hover:underline">
            Talk to our commercial team
          </Link>
        </p>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, testid, placeholder, type = "text" }) => (
  <label className="block">
    <span className="text-[11px] font-medium uppercase tracking-wider text-[var(--tt-muted)]">{label}</span>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-1 w-full rounded-xl border border-[var(--tt-line)] bg-white px-3.5 py-3 text-sm outline-none focus:border-[var(--tt-navy)] transition"
    />
  </label>
);

const Select = ({ label, value, onChange, options, testid }) => (
  <label className="block">
    <span className="text-[11px] font-medium uppercase tracking-wider text-[var(--tt-muted)]">{label}</span>
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full rounded-xl border border-[var(--tt-line)] bg-white px-3.5 py-3 text-sm outline-none focus:border-[var(--tt-navy)] transition"
    >
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  </label>
);

export default Quote;
